import axios from 'axios';

/**
 * Refresh the access token for SSE reconnection.
 *
 * Native EventSource bypasses axios, so the global 401 → silent-refresh
 * interceptor in api.ts never fires for SSE. This helper is used inside
 * EventSource `onerror` handlers to refresh the token manually before
 * rebuilding the connection.
 *
 * Uses raw axios (not apiClient) to avoid re-entering the interceptor.
 * The backend rotates refresh tokens on `/auth/refresh`, so both the
 * access and refresh tokens are persisted to localStorage on success.
 *
 * Returns the new access token, or null if the refresh failed (no
 * refresh token, network error, or 401 from an expired/revoked
 * refresh token). Callers should fall back to polling or redirect
 * to login when null is returned.
 *
 * Implements single-flight deduplication: parallel calls share a single
 * network request. Also throttles to at most one network request per 5 seconds;
 * calls within the throttle window return the current cached token instead.
 */

// Module-level state for single-flight and throttling
let inflight: Promise<string | null> | null = null;
let lastRefreshAt = 0;
const MIN_REFRESH_INTERVAL_MS = 5_000;

export async function refreshAccessTokenForSSE(): Promise<string | null> {
  // Single-flight: if a refresh is already in progress, return that promise
  if (inflight) return inflight;

  // Throttle: if we refreshed recently, return the current cached token
  if (Date.now() - lastRefreshAt < MIN_REFRESH_INTERVAL_MS) {
    return localStorage.getItem('token');
  }

  // Perform the refresh, updating state in the finally block
  inflight = doRefresh()
    .finally(() => {
      lastRefreshAt = Date.now();
      inflight = null;
    });

  return inflight;
}

/**
 * Internal: perform the actual token refresh request and update storage.
 * Called by refreshAccessTokenForSSE after single-flight and throttle checks.
 */
async function doRefresh(): Promise<string | null> {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return null;

  try {
    const response = await axios.post('/api/v1/auth/refresh', {
      refresh_token: refreshToken,
    });
    const newAccessToken = response.data?.access_token;
    const newRefreshToken = response.data?.refresh_token;
    if (!newAccessToken) return null;

    localStorage.setItem('token', newAccessToken);
    if (newRefreshToken) {
      localStorage.setItem('refresh_token', newRefreshToken);
    }
    return newAccessToken;
  } catch {
    return null;
  }
}
